import React, { useState, useEffect, useRef, useContext } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { getCerebroWelcome, getCerebroStarters, getCerebroModeName, getCerebroModeDescription, getSuggestedQuestionsLabel, getCerebroThinkingLabel } from "@/utils/cerebroTranslations";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Brain,
  Send,
  Bot,
  User,
  Sparkles,
  Loader2,
  Settings,
  Trash2,
  ChevronDown,
  Briefcase,
  GraduationCap,
  Microscope,
  LineChart,
  Code,
  Palette,
  Heart,
  Dumbbell,
  Users,
  Lightbulb,
  MessageSquare,
  Zap,
  StopCircle
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Cerebro conversation modes
const CEREBRO_MODES = [
  {
    id: "everyday",
    name: "Everyday Genius",
    description: "Life + Work + Thinking",
    icon: Sparkles,
    color: "text-cyan-400",
    bgColor: "bg-cyan-500/10",
    systemPrompt: `Act as Everyday Genius.

You are a hyper efficient general purpose assistant for daily life, work, and thinking.

Goals:
- Solve problems immediately
- Provide practical answers
- Keep responses short
- Adapt tone automatically to context
- Suggest next steps
- Offer 2-3 options when useful

Style: Friendly, crisp, direct. No filler.

FORMATTING: Use numbered lists (1. at START), **bold** for key points, clear paragraphs.

Stream output immediately. Prefer partial responses over waiting.`
  },
  {
    id: "vision",
    name: "Vision Mode",
    description: "Ideas + Strategy + Future",
    icon: Lightbulb,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    systemPrompt: `Act as Vision Mode.

You are a strategic thinker focused on ideas, business, futures, and long term planning.

Goals:
- Lead with conclusions
- Structure complex topics quickly
- Use frameworks
- Present upside and downside
- Identify leverage points
- Flag risks early

Style: Executive, precise, confident. No motivational fluff.

FORMATTING: Use numbered lists (1. at START), **bold** for frameworks, bullet lists preferred.

Stream immediately. Compress reasoning.`
  },
  {
    id: "builder",
    name: "Builder Mode",
    description: "Code + Systems",
    icon: Code,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    systemPrompt: `Act as Builder Mode.

You are an elite engineer and systems architect optimized for implementation speed.

Goals:
- Output working code first
- Minimize explanations unless asked
- Default to production ready solutions
- Use sensible assumptions
- Generate complete runnable examples

Rules:
- If requirements unclear, ask one precise question then proceed with best assumptions
- Follow modern best practices
- Avoid unnecessary abstractions

Style: Technical, concise, implementation focused.

FORMATTING: Use code blocks, numbered steps (1. at START), **bold** for important notes.

Begin streaming immediately. Emit partial code blocks while continuing.`
  }
];

const Cerebro = () => {
  const { user } = useContext(AuthContext);
  const { language } = useLanguage();
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [model, setModel] = useState("auto");
  const [mode, setMode] = useState(CEREBRO_MODES[0]);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const latestMessageRef = useRef(null);
  const abortControllerRef = useRef(null);
  const timingRef = useRef({});
  const updateBufferRef = useRef({ text: "", messageId: null });
  const rafIdRef = useRef(null);

  const isDemo = new URLSearchParams(location.search).get("demo") === "true";

  useEffect(() => {
    // Set welcome message with pre-translated content (instant)
    const welcome = getCerebroWelcome(mode.id, language);
    const starters = getCerebroStarters(mode.id, language);
    
    setMessages([{
      role: "assistant",
      content: welcome,
      timestamp: new Date().toISOString(),
      model: "system",
      followup_questions: starters
    }]);
  }, [mode, language]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    // Aggressive auto-focus with multiple attempts
    const focusInput = () => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.click();
      }
    };
    
    // Try immediately
    focusInput();
    
    // Try again after 100ms
    const timer1 = setTimeout(focusInput, 100);
    
    // Try again after 500ms
    const timer2 = setTimeout(focusInput, 500);
    
    // Try again after 1000ms
    const timer3 = setTimeout(focusInput, 1000);
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);


  const getWelcomeMessage = (selectedMode) => {
    const greetings = {
      executive: "Welcome. I'm Cerebro, your strategic advisor. Whether you're navigating complex decisions, evaluating opportunities, or shaping organizational direction — I'm here to provide the clarity and insight you need. What strategic challenge can I help you with today?",
      scientist: "Hello, I'm Cerebro in Science Mode. I'm here to help you explore scientific questions with rigor and precision. Whether it's understanding mechanisms, evaluating evidence, or designing experiments — let's pursue knowledge together. What would you like to investigate?",
      researcher: "Welcome, I'm Cerebro in Research Mode. I can help with literature reviews, methodology design, research questions, and scholarly analysis. Let's explore your research interests with academic rigor. What are you investigating?",
      analyst: "Hello, I'm Cerebro in Analyst Mode. I excel at extracting insights from data, identifying patterns, and building analytical frameworks. Whether it's metrics, trends, or performance analysis — I'm ready to transform information into intelligence. What would you like to analyze?",
      engineer: "Welcome, I'm Cerebro in Engineer Mode. I specialize in technical problem-solving, systems design, and implementation. From architecture to optimization — I'll help you build robust solutions. What technical challenge are you tackling?",
      creative: "Hello, I'm Cerebro in Creative Mode. I'm here to help you brainstorm, ideate, and explore unconventional possibilities. Whether it's a creative project, naming, or breakthrough thinking — let's imagine together. What shall we create?",
      coach: "Welcome, I'm Cerebro in Coach Mode. I'm here to support your personal growth, help clarify goals, and navigate life's challenges. Let's work together on building the life you envision. What would you like to focus on?",
      athlete: "Hello, I'm Cerebro in Athletic Mode. I specialize in training optimization, sports science, and peak performance. Whether it's programming, nutrition, or mental game — I'll help you reach your athletic potential. What's your performance goal?",
      mentor: "Welcome, I'm Cerebro in Mentor Mode. I provide guidance on career development, professional growth, and workplace navigation. Let's discuss your career path and opportunities. What professional question can I help with?",
      innovator: "Hello, I'm Cerebro in Innovation Mode. I explore emerging technologies, future trends, and paradigm shifts. Let's envision what's possible and identify opportunities at the cutting edge. What future are you curious about?"
    };
    return greetings[selectedMode.id] || greetings.executive;
  };

  const getStarterQuestions = (selectedMode) => {
    const starters = {
      executive: [
        "What are the top 3 strategic priorities for scaling a B2B SaaS company in 2025?",
        "How should I approach a potential M&A opportunity with limited due diligence time?",
        "What metrics should a CEO track weekly to ensure organizational health?"
      ],
      scientist: [
        "Explain the current state of quantum computing and its practical applications",
        "What are the most promising approaches to developing AGI?",
        "How do CRISPR gene editing mechanisms work at the molecular level?"
      ],
      researcher: [
        "What methodology should I use for studying user behavior in digital products?",
        "How do I design a robust literature review for my thesis?",
        "What statistical tests are appropriate for comparing two treatment groups?"
      ],
      analyst: [
        "What KPIs should I track for a SaaS product's growth?",
        "How do I identify anomalies in time-series sales data?",
        "What visualization best shows customer churn patterns?"
      ],
      engineer: [
        "How should I architect a real-time chat application for 1M users?",
        "What's the best approach for optimizing database queries in production?",
        "How do I design a fault-tolerant distributed system?"
      ],
      creative: [
        "Help me brainstorm unique product names for an AI robotics company",
        "What's a creative metaphor for explaining blockchain to non-technical people?",
        "Give me 10 unconventional marketing campaign ideas"
      ],
      coach: [
        "How do I overcome procrastination and build consistent habits?",
        "What's a framework for making difficult life decisions?",
        "How can I improve my work-life balance as a founder?"
      ],
      athlete: [
        "Design a 12-week strength training program for me",
        "How should I periodize my training for peak performance?",
        "What's the optimal nutrition strategy for endurance athletes?"
      ],
      mentor: [
        "Should I take this job offer or stay at my current company?",
        "How do I negotiate a senior role transition and compensation?",
        "What skills should I develop to move into executive leadership?"
      ],
      innovator: [
        "What are the most disruptive technologies emerging in 2025?",
        "How will AI transform healthcare in the next 5 years?",
        "What opportunities exist at the intersection of robotics and biotech?"
      ]
    };
    return starters[selectedMode.id] || starters.executive;
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    const starterQuestions = getStarterQuestions(newMode);
    // Add a mode switch message
    setMessages(prev => [...prev, {
      role: "system",
      content: `Switched to ${newMode.name}`,
      timestamp: new Date().toISOString()
    }, {
      role: "assistant",
      content: getWelcomeMessage(newMode),
      timestamp: new Date().toISOString(),
      model: "system",
      followup_questions: starterQuestions
    }]);
    toast.success(`Switched to ${newMode.name}`);
  };

  const formatResponse = (text) => {
    if (!text) return text;
    
    let formatted = text
      // Fix numbers appearing at end of lines - move them to start
      .replace(/(.+?)\s+(\d+\.)\s*$/gm, '\n\n$2 $1')
      // Ensure numbered items start on new line with number first
      .replace(/(\d+\.)\s+/g, '\n\n$1 ')
      // Add line break before bullet points
      .replace(/\s+•\s+/g, '\n\n• ')
      // Add paragraph breaks between sentences
      .replace(/([.!?])\s+(?!•|\d)([A-Z])/g, '$1\n\n$2')
      // Clean up extra newlines
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    
    return formatted;
  };
  
  // Render text with bold support
  const renderFormattedText = (text) => {
    if (!text) return text;
    
    // Split by **bold** markers
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    
    return parts.map((part, idx) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        // Bold text
        return <strong key={idx} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date().toISOString()
    };

    const userInput = input;
    const selectedModel = model;  // Capture model at send time
    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    // IMMEDIATELY scroll to show thinking dots
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const scrollElement = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
        if (scrollElement) {
          scrollElement.scrollTop = scrollElement.scrollHeight;
        }
      });
    });

    // Don't add any message box yet - the bouncing dots will show while thinking
    let messageAdded = false;
    const messageId = Date.now();

    try {
      const res = await fetch(`${API}/cerebro/chat`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "X-User-Language": language
        },
        credentials: "include",
        body: JSON.stringify({
          message: userInput,
          session_id: sessionId,
          model: selectedModel,
          mode: mode.id,
          system_prompt: mode.systemPrompt
        })
      });

      // Handle streaming response
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let fullText = "";
      let currentSessionId = sessionId;
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        
        // Keep the last incomplete line in buffer
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.trim().startsWith('data: ')) {
            try {
              const jsonStr = line.trim().slice(6);
              const data = JSON.parse(jsonStr);
              
              console.log('SSE event:', data.type, data);
              
              if (data.type === 'thinking') {
                // Update or add thinking message
                setMessages(prev => {
                  const thinkingExists = prev.some(m => m.role === 'thinking' && m.messageId === messageId + '_thinking');
                  if (thinkingExists) {
                    return prev.map(msg => 
                      msg.messageId === messageId + '_thinking'
                        ? { ...msg, content: data.message, stage: data.stage }
                        : msg
                    );
                  } else {
                    return [...prev, {
                      role: "thinking",
                      content: data.message,
                      stage: data.stage,
                      timestamp: new Date().toISOString(),
                      messageId: messageId + '_thinking'
                    }];
                  }
                });
                
                // Auto-scroll
                setTimeout(() => {
                  const scrollElement = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
                  if (scrollElement) scrollElement.scrollTop = scrollElement.scrollHeight;
                }, 50);
              } else if (data.type === 'start') {
                currentSessionId = data.session_id;
                if (!sessionId) setSessionId(data.session_id);
                // Log which model was auto-selected
                if (data.model_selected) {
                  console.log('Auto-selected model:', data.model_selected);
                }
              } else if (data.type === 'chunk') {
                fullText = data.full_text;
                
                // Add message box on first chunk AND hide bouncing dots AND remove thinking message
                if (!messageAdded) {
                  console.log('First chunk - hiding dots, removing thinking, showing message');
                  setLoading(false); // Hide bouncing dots immediately
                  
                  // Remove thinking message
                  setMessages(prev => prev.filter(m => m.messageId !== messageId + '_thinking'));
                  
                  setMessages(prev => [...prev, {
                    role: "assistant",
                    content: formatResponse(fullText),
                    timestamp: new Date().toISOString(),
                    model: selectedModel,  // Use captured model
                    messageId
                  }]);
                  messageAdded = true;
                  
                  // Auto-scroll to response
                  setTimeout(() => {
                    if (latestMessageRef.current) {
                      latestMessageRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start'
                      });
                    }
                  }, 100);
                } else {
                  // Batch updates every 3 words to reduce re-renders
                  const wordCount = fullText.split(' ').length;
                  if (wordCount % 3 === 0 || data.full_text.endsWith('.') || data.full_text.endsWith('!') || data.full_text.endsWith('?')) {
                    setMessages(prev => prev.map(msg => 
                      msg.messageId === messageId 
                        ? { ...msg, content: formatResponse(fullText) }
                        : msg
                    ));
                  }
                }
              } else if (data.type === 'done') {
                // Final update with correct model and follow-up questions
                console.log('Done event - followup questions:', data.followup_questions);
                setMessages(prev => prev.map(msg => 
                  msg.messageId === messageId 
                    ? {
                        ...msg,
                        content: formatResponse(fullText),
                        model: selectedModel,  // Use captured model
                        followup_questions: data.followup_questions || []
                      }
                    : msg
                ));
              } else if (data.type === 'error') {
                throw new Error(data.message);
              }
            } catch (parseError) {
              console.log('Parse error:', parseError);
            }
          }
        }
      }

    } catch (e) {
      console.error("Chat error:", e);
      toast.error("Failed to get response");
      
      // Add error message
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I apologize, but I encountered an error processing your request. Please try again.",
        timestamp: new Date().toISOString(),
        model: "error"
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
      
      // Cleanup RAF
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setLoading(false);
      toast.success("Response stopped");
    }
  };

  const clearChat = () => {
    setMessages([{
      role: "assistant",
      content: getWelcomeMessage(mode),
      timestamp: new Date().toISOString(),
      model: "system"
    }]);
    setSessionId(null);
    toast.success("Conversation cleared");
  };

  const getModelBadge = (modelName) => {
    // Handle both request models and display names from backend
    const lowerModel = (modelName || "").toLowerCase();
    
    if (lowerModel.includes("gpt") || lowerModel === "openai") {
      return { label: modelName.includes("Mini") || modelName.includes("Nano") ? modelName : "GPT-5 Mini", color: "bg-green-500/10 text-green-400 border-green-500/20" };
    } else if (lowerModel.includes("grok")) {
      return { label: "Grok", color: "bg-purple-500/10 text-purple-400 border-purple-500/20" };
    } else if (lowerModel.includes("deepseek") || lowerModel.includes("4.1")) {
      return { label: "DeepSeek", color: "bg-red-500/10 text-red-400 border-red-500/20" };
    } else if (lowerModel.includes("claude") || lowerModel === "anthropic") {
      return { label: modelName.includes("Haiku") ? "Claude Haiku" : "Claude", color: "bg-orange-500/10 text-orange-400 border-orange-500/20" };
    } else if (lowerModel.includes("gemini") || lowerModel === "gemini") {
      return { label: modelName.includes("Flash") ? modelName : "Gemini", color: "bg-blue-500/10 text-blue-400 border-blue-500/20" };
    } else if (lowerModel === "mock") {
      return { label: "Mock", color: "bg-gray-500/10 text-gray-400 border-gray-500/20" };
    } else {
      return { label: "Cerebro", color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20" };
    }
  };

  const ModeIcon = mode.icon;

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col px-2 sm:px-0" data-testid="cerebro-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${mode.bgColor} flex items-center justify-center flex-shrink-0`}>
            <ModeIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${mode.color}`} />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="font-heading text-lg sm:text-2xl lg:text-3xl font-bold text-white flex items-center gap-2">
              <span className="truncate">Cerebro</span>
              <Badge className={`${mode.bgColor} ${mode.color} border-0 font-normal text-[10px] sm:text-xs flex-shrink-0`}>
                {getCerebroModeName(mode.id, language)}
              </Badge>
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm truncate">{getCerebroModeDescription(mode.id, language)}</p>
          </div>
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
          {/* Mode Selector */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="border-white/10 bg-[#0A0A0A] hover:bg-white/5 gap-1 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4"
                data-testid="mode-selector"
              >
                <ModeIcon className={`w-3 h-3 sm:w-4 sm:h-4 ${mode.color}`} />
                <span className="hidden sm:inline">{getCerebroModeName(mode.id, language)}</span>
                <ChevronDown className="w-3 h-3 text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 sm:w-72 bg-[#0A0A0A] border-white/10">
              <DropdownMenuLabel className="text-gray-400 text-xs uppercase tracking-wider">
                Conversation Style
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              {CEREBRO_MODES.map((m) => {
                const Icon = m.icon;
                return (
                  <DropdownMenuItem
                    key={m.id}
                    onClick={() => handleModeChange(m)}
                    className={`flex items-start gap-3 p-3 cursor-pointer ${
                      mode.id === m.id ? 'bg-white/5' : ''
                    } hover:bg-white/5 focus:bg-white/5`}
                  >
                    <div className={`w-8 h-8 rounded-lg ${m.bgColor} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-4 h-4 ${m.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm">{getCerebroModeName(m.id, language)}</p>
                      <p className="text-gray-500 text-xs truncate">{getCerebroModeDescription(m.id, language)}</p>
                    </div>
                    {mode.id === m.id && (
                      <Zap className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button 
            variant="outline" 
            size="icon"
            onClick={clearChat}
            className="border-white/10 hover:bg-white/5 w-8 h-8 sm:w-10 sm:h-10"
            data-testid="clear-chat-btn"
          >
            <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
          </Button>
          
          {loading && (
            <Button
              variant="outline"
              size="icon"
              onClick={handleStop}
              className="border-red-500/30 hover:bg-red-500/10 text-red-400 w-8 h-8 sm:w-10 sm:h-10"
              title="Stop generation"
            >
              <StopCircle className="w-3 h-3 sm:w-4 sm:h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Chat container */}
      <Card className="flex-1 bg-[#0A0A0A] border-white/10 flex flex-col overflow-hidden">
        {/* Messages area */}
        <ScrollArea className="flex-1 p-2 sm:p-4" ref={scrollRef}>
          <div className="space-y-3 sm:space-y-4 max-w-4xl mx-auto">
            {messages.map((message, idx) => {
              // Thinking process messages (NEW - with stages)
              if (message.role === 'thinking' && !message.isThinking) {
                return (
                  <div key={idx} className="flex gap-2 sm:gap-3 mb-2 sm:mb-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 animate-spin" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="glass border border-purple-500/30 rounded-xl sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-3">
                        <p className="text-xs sm:text-sm text-purple-200">
                          {message.content}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              }
              
              // Old thinking process messages
              if (message.role === 'thinking' && message.isThinking) {
                return (
                  <div key={idx} className="flex gap-2 sm:gap-4 mb-2 sm:mb-4">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-base sm:text-lg animate-pulse">💭</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="glass border border-purple-500/30 rounded-xl sm:rounded-2xl px-3 py-2 sm:px-5 sm:py-4">
                        <p className="text-xs sm:text-sm text-purple-200 whitespace-pre-wrap break-words">
                          {renderFormattedText(message.content)}
                        </p>
                      </div>
                      <p className="text-[10px] text-gray-600 mt-1.5">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                );
              }

              // System stage messages
              if (message.role === 'system') {
                return (
                  <div key={idx} className="flex justify-center">
                    <Badge variant="outline" className="border-white/10 text-gray-500 text-[10px] sm:text-xs font-normal">
                      {message.content}
                    </Badge>
                  </div>
                );
              }
              
              return (
                <div 
                  key={idx}
                  ref={idx === messages.length - 1 && message.role === 'assistant' ? latestMessageRef : null}
                  className={`flex gap-2 sm:gap-4 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                  data-testid={`message-${idx}`}
                >
                  <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    message.role === 'user' 
                      ? 'bg-cyan-500/20' 
                      : mode.bgColor
                  }`}>
                    {message.role === 'user' ? (
                      <User className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
                    ) : (
                      <ModeIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${mode.color}`} />
                    )}
                  </div>
                  
                  <div className={`flex-1 min-w-0 ${message.role === 'user' ? 'text-right' : ''}`}>
                    <div className={`inline-block text-left max-w-full ${
                      message.role === 'user' 
                        ? 'bg-cyan-500/10 border border-cyan-500/20' 
                        : 'glass'
                    } rounded-xl sm:rounded-2xl px-3 py-2 sm:px-5 sm:py-4`}>
                      {message.role === 'assistant' && message.model !== 'system' && (
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {message.multi_model && message.models_used ? (
                            <>
                              <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-[10px] sm:text-xs">
                                <Sparkles className="w-2 h-2 sm:w-3 sm:h-3 mr-1" />
                                Multi-Model
                              </Badge>
                              {message.models_used.slice(0, 3).map((model, i) => (
                                <Badge key={i} className="bg-cyan-500/10 text-cyan-400 border-cyan-500/20 text-[10px] sm:text-xs">
                                  {model}
                                </Badge>
                              ))}
                              {message.models_used.length > 3 && (
                                <Badge className="bg-gray-500/10 text-gray-400 border-gray-500/20 text-[10px] sm:text-xs">
                                  +{message.models_used.length - 3}
                                </Badge>
                              )}
                            </>
                          ) : (
                            <Badge className={`${getModelBadge(message.model).color} text-[10px] sm:text-xs`}>
                              {getModelBadge(message.model).label}
                            </Badge>
                          )}
                        </div>
                      )}
                      <p className={`text-sm sm:text-[15px] leading-relaxed whitespace-pre-wrap break-words ${
                        message.role === 'user' ? 'text-white' : 'text-gray-200'
                      }`}>
                        {renderFormattedText(message.content)}
                      </p>
                      {/* Follow-up questions - more readable */}
                      {message.role === 'assistant' && message.followup_questions && message.followup_questions.length > 0 && (
                        <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-cyan-500/10">
                          <p className="text-xs sm:text-sm font-medium text-gray-400 mb-2 sm:mb-3 flex items-center gap-2">
                            <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-cyan-400" />
                            {getSuggestedQuestionsLabel(language)}
                          </p>
                          <div className="flex flex-col gap-2 sm:gap-3">
                            {message.followup_questions.map((question, qIdx) => (
                              <button
                                key={qIdx}
                                onClick={async () => {
                                  if (loading) return;
                                  console.log('Submitting followup:', question);
                                  
                                  // Immediately add user message
                                  const userMsg = {
                                    role: "user",
                                    content: question,
                                    timestamp: new Date().toISOString()
                                  };
                                  setMessages(prev => [...prev, userMsg]);
                                  setLoading(true);
                                  
                                  // Immediate scroll to very bottom to show the question
                                  requestAnimationFrame(() => {
                                    if (scrollRef.current) {
                                      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
                                      if (scrollElement) {
                                        scrollElement.scrollTop = scrollElement.scrollHeight;
                                      }
                                    }
                                  });
                                  
                                  // Send to backend
                                  let messageAdded = false;
                                  const msgId = Date.now();
                                  
                                  try {
                                    const res = await fetch(`${API}/cerebro/chat`, {
                                      method: "POST",
                                      headers: { "Content-Type": "application/json" },
                                      credentials: "include",
                                      body: JSON.stringify({
                                        message: question,
                                        session_id: sessionId,
                                        model: model,
                                        mode: mode.id,
                                        system_prompt: mode.systemPrompt
                                      })
                                    });
                                    
                                    const reader = res.body.getReader();
                                    const decoder = new TextDecoder();
                                    let fullText = "";
                                    let buffer = "";
                                    
                                    while (true) {
                                      const { done, value } = await reader.read();
                                      if (done) break;
                                      
                                      buffer += decoder.decode(value, { stream: true });
                                      const lines = buffer.split('\n');
                                      buffer = lines.pop() || "";
                                      
                                      for (const line of lines) {
                                        if (line.trim().startsWith('data: ')) {
                                          try {
                                            const data = JSON.parse(line.trim().slice(6));
                                            
                                            if (data.type === 'start' && !sessionId) {
                                              setSessionId(data.session_id);
                                            } else if (data.type === 'chunk') {
                                              fullText = data.full_text;
                                              
                                              if (!messageAdded) {
                                                setLoading(false);
                                                setMessages(prev => [...prev, {
                                                  role: "assistant",
                                                  content: formatResponse(fullText),
                                                  timestamp: new Date().toISOString(),
                                                  model: model,
                                                  messageId: msgId
                                                }]);
                                                messageAdded = true;
                                              } else {
                                                // Batch updates every 3 words to reduce re-renders
                                                const wordCount = fullText.split(' ').length;
                                                if (wordCount % 3 === 0 || fullText.endsWith('.') || fullText.endsWith('!') || fullText.endsWith('?')) {
                                                  setMessages(prev => prev.map(msg => 
                                                    msg.messageId === msgId 
                                                      ? { ...msg, content: formatResponse(fullText) }
                                                      : msg
                                                  ));
                                                }
                                              }
                                            } else if (data.type === 'done') {
                                              setMessages(prev => prev.map(msg => 
                                                msg.messageId === msgId 
                                                  ? {
                                                      ...msg,
                                                      content: formatResponse(fullText),
                                                      followup_questions: data.followup_questions || []
                                                    }
                                                  : msg
                                              ));
                                            }
                                          } catch (e) {
                                            console.log('Parse error:', e);
                                          }
                                        }
                                      }
                                    }
                                  } catch (e) {
                                    console.error('Followup error:', e);
                                  } finally {
                                    setLoading(false);
                                  }
                                }}
                                className="text-left text-base text-white bg-white/5 hover:bg-cyan-500/10 border border-white/10 hover:border-cyan-500/30 rounded-xl px-4 py-3.5 transition-all group cursor-pointer"
                              >
                                <span className="flex items-center gap-3">
                                  <MessageSquare className="w-4 h-4 text-cyan-400 opacity-70 group-hover:opacity-100 flex-shrink-0" />
                                  <span className="leading-relaxed">{question}</span>
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className={`text-[10px] text-gray-600 mt-1.5 ${message.role === 'user' ? 'text-right' : ''}`}>
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="flex gap-4">
                <div className={`w-10 h-10 rounded-xl ${mode.bgColor} flex items-center justify-center`}>
                  <ModeIcon className={`w-5 h-5 ${mode.color}`} />
                </div>
                <div className="glass rounded-2xl px-5 py-4 flex-1">
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                      <span className="text-gray-400 text-sm">{getCerebroThinkingLabel(language)}</span>
                    </div>
                    
                    {/* Engaging content while waiting */}
                    <div className="text-sm text-gray-400 space-y-2 animate-pulse">
                      <p>💭 Analyzing your question...</p>
                      <p>🔍 Considering context and best approach...</p>
                      <p>📝 Crafting a helpful response...</p>
                      <div className="w-32 h-1 bg-cyan-400/20 rounded-full overflow-hidden mt-3">
                        <div className="h-full bg-cyan-400 rounded-full animate-progress" style={{
                          animation: 'progress 3s ease-in-out infinite'
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <style>{`
              @keyframes progress {
                0% { width: 0%; }
                50% { width: 70%; }
                100% { width: 95%; }
              }
            `}</style>
          </div>
        </ScrollArea>

        {/* Input area */}
        <div className="p-2 sm:p-4 border-t border-white/10 bg-[#050505]">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-2 sm:gap-3 items-end">
              <div className="flex-1 relative min-w-0">
                <Textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyPress}
                  placeholder="Message Cerebro..."
                  className="min-h-[44px] sm:min-h-[52px] max-h-[200px] resize-none bg-[#0A0A0A] border-white/10 text-white placeholder:text-gray-500 rounded-xl px-3 sm:px-4 py-2.5 sm:py-3.5 pr-12 text-sm sm:text-base"
                  disabled={loading}
                  data-testid="chat-input"
                  rows={1}
                />
              </div>
              <Button 
                onClick={handleSend}
                disabled={loading || !input.trim()}
                size="icon"
                className="w-[44px] h-[44px] sm:w-[52px] sm:h-[52px] rounded-xl bg-cyan-500 hover:bg-cyan-400 text-black flex-shrink-0"
                data-testid="send-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                )}
              </Button>
            </div>
            <p className="text-center text-[10px] sm:text-[11px] text-gray-600 mt-1.5 sm:mt-2 px-2">
              Cerebro can make mistakes. Consider checking important information.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Cerebro;
